import React, { useMemo, useState } from "react";
import { Mui } from "@osu/react-ui";
import {
  MRT_ShowHideColumnsMenuItems,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton,
  getDefaultColumnOrderIds,
} from "material-react-table";

export function ToolbarInternalActionsWithReset({ table, onResetColumns }) {
  const {
    options: {
      columnFilterDisplayMode,
      enableColumnFilters,
      enableColumnOrdering,
      enableColumnPinning,
      enableDensityToggle,
      enableFilters,
      enableFullScreenToggle,
      enableGlobalFilter,
      enableHiding,
      icons: { ViewColumnIcon },
      localization,
      mrtTheme: { menuBackgroundColor },
    },
    getAllColumns,
    getAllLeafColumns,
    getCenterLeafColumns,
    getIsAllColumnsVisible,
    getIsSomeColumnsPinned,
    getIsSomeColumnsVisible,
    getLeftLeafColumns,
    getRightLeafColumns,
    getState,
    initialState,
  } = table;

  const { columnOrder, columnPinning, density } = getState();
  const [anchorEl, setAnchorEl] = useState(null);
  const [hoveredColumn, setHoveredColumn] = useState(null);

  const openMenu = (event) => setAnchorEl(event.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  const allColumns = useMemo(() => {
    const columns = getAllColumns();
    if (
      columnOrder.length > 0 &&
      !columns.some((col) => col?.columnDef?.columnDefType === "group")
    ) {
      return [
        ...getLeftLeafColumns(),
        ...Array.from(new Set(columnOrder)).map((colId) =>
          getCenterLeafColumns().find((col) => col?.id === colId)
        ),
        ...getRightLeafColumns(),
      ].filter(Boolean);
    }
    return columns;
  }, [columnOrder, columnPinning, getAllColumns, getCenterLeafColumns, getLeftLeafColumns, getRightLeafColumns]);

  const isNestedColumns = allColumns.some(
    (col) => col?.columnDef?.columnDefType === "group"
  );

  const hasColumnOrderChanged = useMemo(() => {
    const initialOrder = initialState?.columnOrder || [];
    if (columnOrder.length !== initialOrder.length) return true;
    return !columnOrder.every((col, idx) => col === initialOrder[idx]);
  }, [columnOrder, initialState?.columnOrder]);

  const handleToggleAllColumns = (value) => {
    getAllLeafColumns()
      .filter((col) => col.columnDef.enableHiding !== false)
      .forEach((col) => col.toggleVisibility(value));
  };

  const handleResetColumns = () => {
    if (typeof onResetColumns === "function") {
      onResetColumns();
    }
  };

  return (
    <Mui.Box sx={{ alignItems: "center", display: "flex", zIndex: 3 }}>
      {enableFilters &&
        enableGlobalFilter &&
        !initialState?.showGlobalFilter && (
          <MRT_ToggleGlobalFilterButton table={table} />
        )}
      {enableFilters &&
        enableColumnFilters &&
        columnFilterDisplayMode !== "popover" && (
          <MRT_ToggleFiltersButton table={table} />
        )}
      {(enableHiding || enableColumnOrdering || enableColumnPinning) && (
        <>
          <Mui.Tooltip title={localization.showHideColumns}>
            <Mui.IconButton
              aria-label={localization.showHideColumns}
              onClick={openMenu}
            >
              <ViewColumnIcon />
            </Mui.IconButton>
          </Mui.Tooltip>
          {anchorEl && (
            <Mui.Menu
              MenuListProps={{
                dense: density === "compact",
                sx: { backgroundColor: menuBackgroundColor },
              }}
              anchorEl={anchorEl}
              disableScrollLock
              onClose={closeMenu}
              open={!!anchorEl}
            >
              <Mui.Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: "0.5rem",
                  pt: 0,
                  gap: 1,
                }}
              >
                {enableHiding && (
                  <Mui.Button
                    disabled={!getIsSomeColumnsVisible()}
                    onClick={() => handleToggleAllColumns(false)}
                  >
                    {localization.hideAll}
                  </Mui.Button>
                )}
                {enableHiding && (
                  <Mui.Button
                    disabled={getIsAllColumnsVisible()}
                    onClick={() => handleToggleAllColumns(true)}
                  >
                    {localization.showAll}
                  </Mui.Button>
                )}
                {enableColumnOrdering && (
                  <Mui.Button
                    onClick={() =>
                      table.setColumnOrder(
                        getDefaultColumnOrderIds(table.options, true)
                      )
                    }
                    disabled={!hasColumnOrderChanged}
                  >
                    {localization.resetOrder}
                  </Mui.Button>
                )}
                <Mui.Button onClick={handleResetColumns} sx={{ ml: 1 }}>
                  Reset Visible
                </Mui.Button>
                {enableColumnPinning && (
                  <Mui.Button
                    disabled={!getIsSomeColumnsPinned()}
                    onClick={() => table.resetColumnPinning(true)}
                  >
                    {localization.unpinAll}
                  </Mui.Button>
                )}
              </Mui.Box>
              <Mui.Divider />
              {allColumns.map((column, index) => (
                <MRT_ShowHideColumnsMenuItems
                  allColumns={allColumns}
                  column={column}
                  hoveredColumn={hoveredColumn}
                  isNestedColumns={isNestedColumns}
                  key={`${index}-${column.id}`}
                  setHoveredColumn={setHoveredColumn}
                  table={table}
                />
              ))}
            </Mui.Menu>
          )}
        </>
      )}
      {enableDensityToggle && <MRT_ToggleDensePaddingButton table={table} />}
      {enableFullScreenToggle && <MRT_ToggleFullScreenButton table={table} />}
    </Mui.Box>
  );
}
